/**
 * Tipos de Usuario — BackOffice.
 *
 * Sustituye al reparto de permisos usuario por usuario, que produjo CUATRO
 * formas distintas de permisos conviviendo en la misma base.
 *
 * Las tres reglas que la pantalla REFLEJA (no aplica — la autorización real vive
 * en el servidor, aquí solo se decide qué mostrar):
 *
 *   TECHO    Un módulo que el plan del tenant no incluye se pinta apagado y no
 *            se puede elegir. Si un tipo ya lo tenía, se avisa que está inerte.
 *
 *   ESPEJO   Solo se ofrecen los accesos que el propio usuario tiene. El
 *            servidor manda `grantable`; lo que está fuera se muestra en gris
 *            con su motivo, en vez de dejar elegir algo que el servidor va a
 *            recortar después sin explicar por qué.
 *
 *   ESCALERA El selector de nivel solo lista `creatable_roles`, que el servidor
 *            calcula según el nivel de quien pregunta.
 *
 * Si el servidor recorta algo al guardar, se muestra exactamente qué se pidió y
 * qué se concedió. Callarlo haría creer al admin que otorgó algo apagado.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { etiquetaNivel } from '@/lib/nivelesUsuario';
// La MISMA lista y el MISMO predicado que dibuja el menú de verdad. Reusarlos es
// deliberado: el aviso de abajo existe para que nadie apague medio sistema sin
// verlo, y un aviso calculado con una copia de la lógica podría mentir justo ahí.
import { NAV_ITEMS, entradaVisible } from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  AlertTriangle, ChevronDown, ChevronRight, Loader2, Lock, Plus, RefreshCcw,
  ShieldCheck, Trash2, Users, X,
} from 'lucide-react';

const COLOR_ACCESO = {
  none: 'text-white/35 border-white/10 bg-white/[0.02]',
  read: 'text-sky-300 border-sky-500/30 bg-sky-500/10',
  write: 'text-amber-300 border-amber-500/30 bg-amber-500/10',
  both: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
};

// La tabla de etiquetas vive en un solo sitio (lib/nivelesUsuario). Estaba copiada en
// cuatro pantallas y todas se olvidaron de `director` al agregarlo.

// Espejo de NIVELES en core/user_types.py. Solo se usa para NO OFRECER en la
// lista a alguien de nivel superior; la autorizacion real la aplica el servidor
// (que ademas rechaza con 403 si se intenta por fuera de la pantalla).
const NIVEL_NUM = { guard: 10, vendor: 20, manager: 50, admin: 80 };

const TIPO_VACIO = { name: '', base_role: 'vendor', scope: {}, components: {} };

/**
 * Qué entradas de MENÚ dejará de ver alguien con este tipo, por su NOMBRE.
 *
 * Función PURA y exportada a propósito: es el aviso que evita que un admin apague
 * medio sistema sin darse cuenta, así que tiene que poder probarse sin navegador.
 *
 * Compara dos escenarios con el predicado REAL del menú (`entradaVisible`): lo que se
 * vería con todo lo que el plan permite, contra lo que se ve con este tipo. La
 * diferencia son las entradas que desaparecen.
 *
 * La única regla del backend que se replica aquí es la definición del propio tipo —el
 * plan es el techo y lo que el tipo no menciona se niega (`resolver_acceso_modulo`)—.
 * La lógica del MENÚ no se replica: se importa.
 */
/**
 * Qué entradas de MENÚ le QUEDAN a alguien con este tipo, por su nombre.
 *
 * Existe por una observación de Alexis: el aviso decía cuántas secciones se van, pero
 * no que la persona podía quedarse SIN NADA. Y el número de las que se van engaña por
 * sí solo — "11 de 20" suena a que quedan 9, cuando en realidad las otras 9 ya estaban
 * escondidas por su nivel. Lo que hay que decir es lo que QUEDA.
 */
export function entradasQueQuedan(tipo, modulos, planModules) {
  if (!tipo || !modulos?.length) return [];
  const claves = modulos.map((m) => m.key);
  const accesos = Object.fromEntries(claves.map((k) => (
    !planModules?.[k] ? [k, 'none'] : [k, tipo.scope?.[k] || 'none']
  )));
  const usuario = { role: tipo.base_role, permissions: {}, allowed_modules: planModules };
  return NAV_ITEMS
    .filter((it) => entradaVisible(it, { user: usuario, impersonating: false, perfil: { modules: accesos } }))
    .map((it) => ({ etiqueta: it.label, esSeccion: !!it.moduleKey }));
}

export function entradasQueDesapareceran(tipo, modulos, planModules) {
  if (!tipo || !modulos?.length) return [];
  const claves = modulos.map((m) => m.key);
  const accesos = (deTipo) => Object.fromEntries(claves.map((k) => {
    if (!planModules?.[k]) return [k, 'none'];                  // el plan manda
    return [k, deTipo ? (tipo.scope?.[k] || 'none') : 'both'];  // y el silencio no otorga
  }));
  // El nivel base importa: cambia qué entradas son solo-de-administración.
  const usuario = { role: tipo.base_role, permissions: {}, allowed_modules: planModules };
  const ver = (mods) => NAV_ITEMS
    .filter((it) => entradaVisible(it, { user: usuario, impersonating: false, perfil: { modules: mods } }))
    .map((it) => it.path);
  const conElPlan = ver(accesos(false));
  const conElTipo = ver(accesos(true));
  return conElPlan
    .filter((ruta) => !conElTipo.includes(ruta))
    .map((ruta) => NAV_ITEMS.find((it) => it.path === ruta)?.label || ruta);
}

/** Qué accesos puede ofrecer el selector para este módulo, dado el techo del actor. */
function opcionesPara(niveles, techo) {
  if (techo == null) return niveles;                     // nivel plataforma: sin techo
  if (techo === 'both') return niveles;
  if (techo === 'none') return niveles.filter((n) => n.key === 'none');
  return niveles.filter((n) => n.key === 'none' || n.key === techo);
}

export default function TiposDeUsuario() {
  const [cargando, setCargando] = useState(true);
  const [catalogo, setCatalogo] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [editando, setEditando] = useState(null);   // null = no hay editor abierto
  const [guardando, setGuardando] = useState(false);
  const [asignando, setAsignando] = useState({});
  const [abiertos, setAbiertos] = useState({});
  const [recortes, setRecortes] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [cat, lista, team] = await Promise.all([
        api.get('/user-types/catalog'),
        api.get('/user-types'),
        // El equipo se pide aquí porque sin él la pantalla no serviría de nada:
        // se podían crear tipos y no había forma de dárselos a nadie.
        api.get('/team').catch(() => ({ data: [] })),
      ]);
      setCatalogo(cat.data);
      setTipos(lista.data?.types || []);
      setEquipo(Array.isArray(team.data) ? team.data : []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudieron cargar los tipos de usuario');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Estabilizado con useMemo: `|| {}` crea un objeto nuevo en cada render, y eso
  // invalidaba los useMemo de abajo constantemente (lo marcó el compilador).
  const planModules = useMemo(() => catalogo?.plan_modules || {}, [catalogo]);
  const grantable = catalogo?.grantable ?? null;
  const niveles = catalogo?.access_levels || [];
  const sinTecho = grantable === null;

  // Módulos que el plan del tenant sí incluye. El resto no se puede repartir
  // porque nadie los tiene: mostrarlos activos sería mentir.
  const modulosVisibles = useMemo(
    () => (catalogo?.modules || []).filter((m) => planModules[m.key]),
    [catalogo, planModules],
  );
  const modulosFueraDelPlan = useMemo(
    () => (catalogo?.modules || []).filter((m) => !planModules[m.key]),
    [catalogo, planModules],
  );

  // ── EL AVISO: qué entradas de MENÚ va a dejar de ver esta persona ──────────
  // Se calcula con NAV_ITEMS y entradaVisible, los mismos que dibujan el menú real,
  // y comparando dos escenarios: "todo lo que el plan permite" contra "lo que este
  // tipo concede". La diferencia son las entradas que desaparecen, POR SU NOMBRE.
  //
  // Se replica aquí una sola regla del backend —el plan es el techo y lo que el tipo
  // no menciona se niega (resolver_acceso_modulo)— porque es la definición del propio
  // tipo. Lo que NO se replica es la lógica del menú: esa se importa.
  const entradasQueDesaparecen = useMemo(
    () => entradasQueDesapareceran(editando, catalogo?.modules, planModules),
    [editando, catalogo, planModules],
  );
  const loQueQueda = useMemo(
    () => entradasQueQuedan(editando, catalogo?.modules, planModules),
    [editando, catalogo, planModules],
  );
  // "Sin nada" no es "cero entradas": Mi Tarjeta Virtual no pertenece a ningún módulo,
  // así que sobrevive a cualquier tipo. Quedarse solo con eso ES quedarse sin menú.
  const seQuedaSinMenu = loQueQueda.length > 0 && !loQueQueda.some((e) => e.esSeccion);
  const seQuedaSinNada = loQueQueda.length === 0;

  // El aviso se muestra siempre, pero además FRENA el primer guardado: hay que
  // verlo y confirmar. Si el tipo cambia, vuelve a frenar (el aviso ya no es el mismo).
  const [avisoVisto, setAvisoVisto] = useState(false);
  useEffect(() => { setAvisoVisto(false); }, [editando?.scope, editando?.components, editando?.base_role]);

  // Un tipo nuevo NACE CON TODO lo que el plan de la empresa permite, y el admin
  // RESTA. Antes nacía vacío y había que enumerar los 22 módulos para no dejar a
  // nadie sin nada: con eso, marcar 3 módulos apagaba los otros 19 sin querer, que
  // es exactamente el accidente que se vio validando. El documento guardado sigue
  // enumerando igual — esto es solo el punto de partida de la pantalla.
  const abrirNuevo = () => {
    const rolPorDefecto = catalogo?.creatable_roles?.includes('vendor')
      ? 'vendor'
      : (catalogo?.creatable_roles?.[0] || 'vendor');
    const todoElPlan = {};
    for (const m of (catalogo?.modules || [])) {
      if (!planModules[m.key]) continue;                  // el plan es el techo
      const techo = sinTecho ? 'both' : (grantable?.[m.key] ?? 'none');
      if (techo !== 'none') todoElPlan[m.key] = techo;    // y nadie reparte lo que no tiene
    }
    setRecortes([]);
    setEditando({ ...TIPO_VACIO, base_role: rolPorDefecto, scope: todoElPlan });
  };

  const abrirExistente = (t) => {
    setRecortes([]);
    setEditando({ ...t, scope: { ...(t.scope || {}) }, components: { ...(t.components || {}) } });
  };

  const ponerModulo = (modulo, valor) => {
    setEditando((e) => {
      const scope = { ...e.scope, [modulo]: valor };
      // Si el módulo queda sin acceso, sus componentes dejan de tener sentido.
      const components = { ...e.components };
      if (valor === 'none') {
        Object.keys(components).forEach((k) => { if (k.startsWith(`${modulo}.`)) delete components[k]; });
      }
      return { ...e, scope, components };
    });
  };

  const ponerComponente = (clave, valor) =>
    setEditando((e) => ({ ...e, components: { ...e.components, [clave]: valor } }));

  const guardar = async () => {
    if (!editando?.name?.trim()) { toast.error('El tipo necesita un nombre'); return; }
    // Primer clic con entradas que se van: no guarda, obliga a leer el aviso. El
    // accidente que esto evita es real: marcar 3 módulos apagó 4 entradas sin querer.
    if (entradasQueDesaparecen.length && !avisoVisto) {
      setAvisoVisto(true);
      toast.warning(`Ojo: esta persona dejará de ver ${entradasQueDesaparecen.length} sección(es). Revisa el aviso y vuelve a guardar.`);
      return;
    }
    setGuardando(true);
    try {
      const cuerpo = {
        name: editando.name.trim(),
        base_role: editando.base_role,
        scope: editando.scope,
        components: editando.components,
      };
      const r = editando.id
        ? await api.put(`/user-types/${editando.id}`, cuerpo)
        : await api.post('/user-types', cuerpo);

      const recortados = r.data?.recortes || [];
      setRecortes(recortados);
      if (recortados.length) {
        toast.warning(`Se guardó, pero ${recortados.length} permiso(s) se recortaron a lo que tú puedes otorgar`);
      } else {
        toast.success(editando.id ? 'Tipo actualizado' : 'Tipo creado');
      }
      if (r.data?.usuarios_afectados > 0) {
        toast.info(`${r.data.usuarios_afectados} usuario(s) quedaron con los permisos nuevos`);
      }
      if (!recortados.length) setEditando(null);
      cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const asignar = async (usuario, tipoId) => {
    if (!tipoId) return;
    const tipo = tipos.find((t) => t.id === tipoId);
    if (!window.confirm(
      `¿Asignar el tipo "${tipo?.name}" a ${usuario.name}?\n\n`
      + `Sus permisos pasan a ser los de ese tipo, y su nivel queda en `
      + `${etiquetaNivel(tipo?.base_role, catalogo?.base_roles)}.`
    )) return;

    setAsignando((s) => ({ ...s, [usuario.id]: true }));
    try {
      await api.post(`/user-types/${tipoId}/assign/${usuario.id}`);
      toast.success(`${usuario.name} quedó como "${tipo?.name}"`);
      cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo asignar');
    } finally {
      setAsignando((s) => ({ ...s, [usuario.id]: false }));
    }
  };

  const eliminar = async (t) => {
    if (!window.confirm(
      `¿Dar de baja el tipo "${t.name}"?\n\nNo se borra: deja de estar disponible y se puede consultar en la bitácora.`
    )) return;
    try {
      await api.delete(`/user-types/${t.id}`);
      toast.success(`"${t.name}" quedó dado de baja`);
      cargar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo dar de baja');
    }
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="tipos-cargando">
        <Loader2 className="w-5 h-5 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tipos-usuario-page">
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#A5D62C]" />
            User tenants
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            Plantillas de permisos para repartir dentro de la empresa (antes Tipos de Usuario).
            Un tipo solo puede{' '}
            <b className="text-white/80">restar</b> sobre lo que el plan ya incluye: nunca otorga
            de más, y nunca alcanza el nivel de plataforma.
            {sinTecho && (
              <span className="text-cyan-300/80"> Estás como Owner: no tienes techo.</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={cargar} className="border-white/10 text-xs" data-testid="tipos-refrescar">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Actualizar
          </Button>
          <Button size="sm" onClick={abrirNuevo}
            className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs" data-testid="tipos-nuevo">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuevo tipo
          </Button>
        </div>
      </div>

      {/* Lista */}
      {!tipos.length && !editando && (
        <Card className="bg-card/50 border-white/5" data-testid="tipos-vacio">
          <CardContent className="p-8 text-center">
            <Users className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-sm text-white/60">Todavía no hay tipos de usuario.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              Mientras no exista ninguno, cada usuario sigue con los permisos que tiene hoy.
              Crear un tipo no cambia nada hasta que se lo asignas a alguien.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {tipos.map((t) => (
          <Card key={t.id} className="bg-card/50 border-white/5" data-testid={`tipo-${t.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-white truncate">{t.name}</h3>
                    <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                      {etiquetaNivel(t.base_role, catalogo?.base_roles)}
                    </Badge>
                    {t.is_system && (
                      <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10">
                        <Lock className="w-2.5 h-2.5 mr-1" /> Del sistema
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {t.usuarios} usuario{t.usuarios === 1 ? '' : 's'} con este tipo
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => abrirExistente(t)}
                    className="h-7 text-[11px] text-white/60 hover:text-white" data-testid={`tipo-editar-${t.id}`}>
                    Editar
                  </Button>
                  {!t.is_system && (
                    <Button variant="ghost" size="sm" onClick={() => eliminar(t)}
                      className="h-7 px-2 text-rose-400/60 hover:text-rose-400" data-testid={`tipo-baja-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Resumen de accesos */}
              <div className="flex flex-wrap gap-1 mt-3">
                {Object.entries(t.scope || {}).filter(([, v]) => v !== 'none').map(([mod, v]) => (
                  <span key={mod} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${COLOR_ACCESO[v]}`}>
                    {(catalogo?.modules || []).find((m) => m.key === mod)?.label || mod}
                  </span>
                ))}
                {!Object.values(t.scope || {}).some((v) => v !== 'none') && (
                  <span className="text-[10px] text-white/30">Sin accesos asignados</span>
                )}
              </div>

              {/* Lo que el plan apaga: se dice, no se esconde */}
              {t.sin_efecto_por_plan?.length > 0 && (
                <div className="flex items-start gap-1.5 mt-3 rounded-md border border-amber-500/20 bg-amber-500/5 px-2 py-1.5"
                  data-testid={`tipo-inerte-${t.id}`}>
                  <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-200/90">
                    Sin efecto porque el plan no los incluye:{' '}
                    <b>{t.sin_efecto_por_plan.join(', ')}</b>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Asignación al equipo — sin esto, crear un tipo no le servía a nadie */}
      {tipos.length > 0 && !editando && (
        <Card className="bg-card/50 border-white/5" data-testid="tipos-asignacion">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-white/50" />
              <h2 className="text-sm font-semibold text-white">Asignar a tu equipo</h2>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">
              Al asignar un tipo, ese usuario pasa a tener exactamente esos permisos.
              Solo aparecen las personas de tu nivel o por debajo.
            </p>

            <div className="space-y-1">
              {equipo
                .filter((m) => (NIVEL_NUM[m.role] ?? 0) <= (catalogo?.my_level ?? 0))
                .map((m) => {
                  const suTipo = tipos.find((t) => t.id === m.user_type_id);
                  const asignables = tipos.filter(
                    (t) => (catalogo?.creatable_roles || []).includes(t.base_role),
                  );
                  return (
                    <div key={m.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-black/10 px-3 py-2"
                      data-testid={`asignar-fila-${m.id}`}>
                      <div className="min-w-0">
                        <p className="text-xs text-white/80 truncate">{m.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {m.email} · {etiquetaNivel(m.role, catalogo?.base_roles)}
                          {suTipo && <span className="text-[#A5D62C]"> · {suTipo.name}</span>}
                          {!suTipo && <span className="text-white/30"> · permisos individuales</span>}
                        </p>
                      </div>
                      <select
                        value={m.user_type_id || ''}
                        disabled={asignando[m.id] || !asignables.length}
                        onChange={(e) => asignar(m, e.target.value)}
                        data-testid={`asignar-${m.id}`}
                        className="h-7 rounded-md bg-black/30 border border-white/10 text-[11px] text-white px-2 shrink-0 disabled:opacity-40 max-w-[190px]">
                        <option value="">Sin tipo (permisos actuales)</option>
                        {asignables.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              {!equipo.length && (
                <p className="text-[11px] text-white/30 py-2">No se pudo cargar el equipo.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor */}
      {editando && (
        <Card className="bg-card/70 border-[#A5D62C]/20" data-testid="tipos-editor">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-white">
                {editando.id ? `Editando "${editando.name}"` : 'Nuevo tipo de usuario'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setEditando(null); setRecortes([]); }}
                className="h-7 px-2 text-white/40 hover:text-white" data-testid="tipos-cerrar-editor">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">Nombre</label>
                <Input value={editando.name} data-testid="tipo-nombre"
                  onChange={(e) => setEditando((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Gerente de piso" className="h-8 text-xs bg-black/20 border-white/10" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  Nivel base <span className="text-white/30">— decide a quién puede crear</span>
                </label>
                <select value={editando.base_role} data-testid="tipo-nivel"
                  onChange={(e) => setEditando((s) => ({ ...s, base_role: e.target.value }))}
                  className="h-8 w-full rounded-md bg-black/20 border border-white/10 text-xs text-white px-2">
                  {(catalogo?.creatable_roles || []).map((r) => (
                    <option key={r} value={r}>{etiquetaNivel(r, catalogo?.base_roles)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Módulos */}
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground">
                Accesos por módulo. Solo aparecen los que el plan de la empresa incluye
                {!sinTecho && ', y solo los niveles que tú mismo tienes'}.
              </p>

              {modulosVisibles.map((m) => {
                const techo = sinTecho ? null : (grantable?.[m.key] ?? 'none');
                const disponibles = opcionesPara(niveles, techo);
                const valor = editando.scope?.[m.key] || 'none';
                const bloqueado = !sinTecho && techo === 'none';
                const tieneComponentes = m.components?.length > 0;
                const abierto = abiertos[m.key];

                return (
                  <div key={m.key} className="rounded-lg border border-white/5 bg-black/10">
                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {tieneComponentes && valor !== 'none' ? (
                          <button onClick={() => setAbiertos((s) => ({ ...s, [m.key]: !abierto }))}
                            className="text-white/30 hover:text-white/70" data-testid={`tipo-exp-${m.key}`}>
                            {abierto ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                        ) : <span className="w-3.5" />}
                        <span className={`text-xs truncate ${bloqueado ? 'text-white/25' : 'text-white/80'}`}>
                          {m.label}
                        </span>
                        {bloqueado && (
                          <span className="text-[9px] text-white/25 shrink-0">— tú no tienes este módulo</span>
                        )}
                      </div>
                      <select value={valor} disabled={bloqueado} data-testid={`tipo-mod-${m.key}`}
                        onChange={(e) => ponerModulo(m.key, e.target.value)}
                        className="h-7 rounded-md bg-black/30 border border-white/10 text-[11px] text-white px-2 shrink-0 disabled:opacity-40">
                        {disponibles.map((n) => (
                          <option key={n.key} value={n.key}>{n.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Componentes del módulo */}
                    {abierto && tieneComponentes && valor !== 'none' && (
                      <div className="border-t border-white/5 px-3 py-2 space-y-1.5">
                        <p className="text-[10px] text-white/30">
                          Sin tocar, cada acción hereda el acceso del módulo.
                        </p>
                        {m.components.map((c) => {
                          const clave = `${m.key}.${c.key}`;
                          return (
                            <div key={clave} className="flex items-center justify-between gap-2 pl-5">
                              <span className="text-[11px] text-white/60 truncate">{c.label}</span>
                              <select value={editando.components?.[clave] ?? ''} data-testid={`tipo-comp-${clave}`}
                                onChange={(e) => ponerComponente(clave, e.target.value)}
                                className="h-6 rounded bg-black/30 border border-white/10 text-[10px] text-white px-1.5 shrink-0">
                                <option value="">Hereda del módulo</option>
                                <option value="none">Sin acceso</option>
                                <option value={c.operacion}>
                                  {c.operacion === 'read' ? 'Solo lectura' : 'Permitido'}
                                </option>
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {modulosFueraDelPlan.length > 0 && (
                <p className="text-[10px] text-white/25 pt-1">
                  {modulosFueraDelPlan.length} módulo(s) no aparecen porque el plan de la empresa
                  no los incluye.
                </p>
              )}
            </div>

            {/* Recortes del servidor */}
            {recortes.length > 0 && (
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3" data-testid="tipos-recortes">
                <p className="text-[11px] text-amber-200 font-medium flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Se guardó, pero estos permisos se recortaron a lo que tú puedes otorgar
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {recortes.map((r, i) => (
                    <li key={i} className="text-[10px] text-amber-200/80">
                      <b>{r.module}</b>: pediste <i>{r.requested}</i> → quedó <i>{r.granted}</i>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {entradasQueDesaparecen.length > 0 && (
              <div className={`rounded-lg border p-3 ${
                // Quedarse sin menu no es "un aviso mas": es dejar a alguien sin
                // sistema. Se distingue a simple vista, no solo por el texto.
                (seQuedaSinMenu || seQuedaSinNada)
                  ? 'border-red-500/60 bg-red-500/15 ring-1 ring-red-500/40'
                  : avisoVisto ? 'border-amber-400/40 bg-amber-500/10' : 'border-white/10 bg-black/20'
              }`}
                data-testid="tipo-aviso-desaparecen">
                {(seQuedaSinMenu || seQuedaSinNada) && (
                  <p className="text-[11px] text-red-300 font-bold mb-1.5" data-testid="tipo-aviso-sin-menu">
                    ⚠️ Esta persona se quedará SIN MENÚ: no le queda ni una sección
                    {seQuedaSinMenu ? ` (solo ${loQueQueda.map((e) => e.etiqueta).join(', ')}, que no es una sección)` : ''}.
                    Podrá entrar y no verá nada que usar.
                  </p>
                )}
                <p className="text-[11px] text-amber-300/90 font-semibold">
                  Con esto, esta persona dejará de ver {entradasQueDesaparecen.length} sección(es) del menú:
                </p>
                <p className="text-[11px] text-white/70 mt-1" data-testid="tipo-aviso-lista">
                  {entradasQueDesaparecen.join(' · ')}
                </p>
                <p className="text-[11px] text-white/70 mt-1.5" data-testid="tipo-aviso-quedan">
                  <span className="text-white/40">Le quedan:</span>{' '}
                  {loQueQueda.filter((e) => e.esSeccion).map((e) => e.etiqueta).join(' · ') || 'ninguna'}
                </p>
                <p className="text-[10px] text-white/35 mt-1.5">
                  Desaparecen del menú: no salen en gris ni dan error. Lo que no marcas, no se concede.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => { setEditando(null); setRecortes([]); }}
                className="border-white/10 text-xs">Cancelar</Button>
              <Button size="sm" onClick={guardar} disabled={guardando} data-testid="tipo-guardar"
                className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs">
                {guardando ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                {entradasQueDesaparecen.length && avisoVisto
                  ? 'Sí, guardar así'
                  : (editando.id ? 'Guardar cambios' : 'Crear tipo')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
